import type { FC } from 'react';
import { useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';

import { TrafficChangeIndicator } from '../../../../../components/TrafficChangeIndicator';
import { InsightType } from '../../../../../domain/insightType';
import type { TrafficChangesSignal, TrafficSignal } from '../../../../../domain/signal';
import { trafficChangesPeriodInMonths } from '../../../../../domain/signal';
import type { SiteTrend } from '../../../../../domain/siteTrend';
import { formatPercentage, formatWithSuffix } from '../../../../../services/numberFormat';
import { formatToMonthYear, secondsToHMS } from '../../../../../services/timeFormat';
import getGraphPeriod from '../getGraphPeriod';

import { makeChartOptions } from './makeChartOptions';
import {
  StyledTrafficChangeDiffContainer,
  StyledTrafficChangeGraphContent,
  StyledTrafficChangeGraphDates,
  StyledTrafficChangeLatestData,
  StyledTrafficChangeLatestDataContainer,
  StyledTrafficChangeLatestDataDate,
  StyledTrafficChangePeriod,
} from './styles';

export const TrafficChangeGraph: FC<{ insight: TrafficSignal | SiteTrend }> = ({ insight }) => {
  const isTrafficChangeSignal = insight.insightType === InsightType.Signal && 'trafficChanges' in insight.filters;

  const siteTrend = insight.insightType === InsightType.SiteTrends ? insight : insight.siteTrends[0];
  const isOneDataPoint = siteTrend.trend.length === 1;

  const chartData = useMemo(() => {
    if (!siteTrend) {
      return [];
    }

    return siteTrend.trend.map((t) => t.value);
  }, [siteTrend]);

  const changeData = useMemo(() => {
    if (!isTrafficChangeSignal) {
      return [];
    }

    const { period } = (insight as TrafficChangesSignal).filters.trafficChanges;
    const periodInMonths = trafficChangesPeriodInMonths[period] + 1;

    return Array.from({ length: chartData.length - periodInMonths })
      .fill(null)
      .concat(chartData.slice(-periodInMonths)) as number[];
  }, [chartData, insight]);

  if (!siteTrend) {
    return null;
  }

  const latestData = () => {
    const latestValue = siteTrend.trend.at(-1)?.value ?? 0;

    if (siteTrend.units === '%' || siteTrend.metric.includes('bounce_rate')) {
      return formatPercentage(latestValue, 2);
    }

    if (siteTrend.units === 's' || siteTrend.metric.includes('visit_duration')) {
      return secondsToHMS(latestValue);
    }

    return formatWithSuffix(latestValue, 1);
  };

  const dataDatePeriod = () => {
    const [startDate, endDate] = getGraphPeriod(siteTrend);

    if (!startDate || !endDate) {
      return;
    }

    return (
      <StyledTrafficChangeGraphDates>
        <span>{startDate}</span>
        <span>{endDate}</span>
      </StyledTrafficChangeGraphDates>
    );
  };

  const trafficPeriod = () => {
    const endDate = siteTrend.trend.at(-1)?.date;
    let startDate: string | Dayjs | undefined = siteTrend.trend.at(0)?.date;

    if (isTrafficChangeSignal) {
      const period = (insight as TrafficChangesSignal).filters.trafficChanges.period;
      const periodInMonths = trafficChangesPeriodInMonths[period];
      startDate = dayjs(endDate).subtract(periodInMonths, 'months');
    }

    if (!startDate || !endDate) {
      return;
    }

    return `${formatToMonthYear(startDate)} - ${formatToMonthYear(endDate)}`;
  };

  const changeValue = () => {
    if (isTrafficChangeSignal) {
      return (insight as TrafficChangesSignal).changeValue;
    }

    const firstValue = siteTrend.trend.at(0)?.value ?? 0;
    const lastValue = siteTrend.trend.at(-1)?.value ?? 0;
    return (lastValue - firstValue) / firstValue;
  };

  const isPositiveMetric = () => {
    return !siteTrend.metric.includes('bounce_rate');
  };

  const getTrafficChangeColor = () => {
    const direction = (insight as TrafficChangesSignal).filters?.trafficChanges?.direction;

    if (!direction) {
      return null;
    }

    if (isPositiveMetric()) {
      return direction === 'grew' ? 'green' : 'red';
    } else {
      return direction === 'grew' ? 'red' : 'green';
    }
  };

  const chartOptions = makeChartOptions(chartData, changeData, getTrafficChangeColor());

  return (
    <StyledTrafficChangeGraphContent>
      <StyledTrafficChangeLatestDataContainer>
        <StyledTrafficChangeLatestData>{latestData()}</StyledTrafficChangeLatestData>
        <StyledTrafficChangeLatestDataDate>
          {formatToMonthYear(siteTrend.trend.at(-1)?.date ?? dayjs())}
        </StyledTrafficChangeLatestDataDate>
      </StyledTrafficChangeLatestDataContainer>
      {!isOneDataPoint ? (
        <StyledTrafficChangeDiffContainer>
          <TrafficChangeIndicator change={changeValue()} greaterIsBetter={isPositiveMetric()} />
          <StyledTrafficChangePeriod>{trafficPeriod()}</StyledTrafficChangePeriod>
        </StyledTrafficChangeDiffContainer>
      ) : null}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      {dataDatePeriod()}
    </StyledTrafficChangeGraphContent>
  );
};
